import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Platform } from '@ionic/angular/standalone';
import {
  autoLoginPartialRoutesGuard,
  OidcSecurityService,
  OpenIdConfiguration,
} from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { WITH_POPUP } from '../auth.provider';
import { InvalidRoleError } from '../errors/invalid-role.error';
import { LoggerService } from '../service/internal/logger.service';
import { OAuthService } from '../service/oauth/oauth.service';
import { UserService } from '../service/user/user.service';
import { CHECK_USER_ROLES } from '../with-user-roles.provider';

export function oauthAuthenticationGuardWithConfig(configId: string): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
    oauthAuthenticationGuard(route, state, configId);
}

//eslint-disable-next-line max-lines-per-function
export async function oauthAuthenticationGuard(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  configId?: string,
): Promise<boolean | UrlTree> {
  const loggerService = inject(LoggerService);
  const oidcSecurityService = inject(OidcSecurityService);
  const userService = inject(UserService);
  const oAuthService = inject(OAuthService);
  const platform = inject(Platform);
  const config =
    oidcSecurityService.getConfigurations().find((item) => item.configId === configId) ??
    oidcSecurityService.getConfigurations()[0];
  const checkUserRolesFn = inject(CHECK_USER_ROLES, { optional: true });
  const isWithPopup = inject(WITH_POPUP, { optional: true });

  let authenticated = false;

  try {
    const response = await firstValueFrom(oidcSecurityService.checkAuthIncludingServer());
    authenticated = response.isAuthenticated;
  } catch (error) {
    authenticated = false;
  }

  try {
    if (!authenticated) {
      if (platform.is('capacitor') && isWithPopup) {
        authenticated = await oAuthService
          .authorizeWithPopUp()
          .then((response) => response?.isAuthenticated ?? false);
      } else {
        authenticated = await firstValueFrom(autoLoginPartialRoutesGuard(route, state, configId), {
          defaultValue: false,
        });
      }
    }

    if (checkUserRolesFn) {
      const promise: void | Promise<void> = checkUserRolesFn(
        await firstValueFrom(userService.currentUser()),
        route,
      );

      if (promise instanceof Promise) {
        await promise;
      }
    }

    return authenticated;
  } catch (error) {
    if (error instanceof InvalidRoleError) {
      return redirectToForbiddenPage(oAuthService, loggerService, config, state);
    }

    loggerService.logError(config, 'Error during authentication guard execution:', error);
    return redirectToUnauthorizedPage(oAuthService, loggerService, config, state);
  }
}

function redirectToUnauthorizedPage(
  oAuthService: OAuthService,
  loggerService: LoggerService,
  config: OpenIdConfiguration,
  state: RouterStateSnapshot,
): boolean | UrlTree {
  const unauthorizedUrlTree = oAuthService.getUnauthorizedUrlTree();
  if (!unauthorizedUrlTree) {
    loggerService.logDebug(
      config,
      `There's no unauthorized route configured, not redirecting to unauthorized page.`,
    );
    return false;
  }
  if (state.url === unauthorizedUrlTree.toString()) {
    loggerService.logDebug(
      config,
      `We're already on the unauthorized page (${unauthorizedUrlTree.toString()}) , not redirecting again.`,
    );

    return false;
  }

  loggerService.logDebug(
    config,
    `User is not authenticated. Redirecting to unauthorized page: ${unauthorizedUrlTree.toString()}.`,
  );
  return unauthorizedUrlTree;
}

function redirectToForbiddenPage(
  oAuthService: OAuthService,
  loggerService: LoggerService,
  config: OpenIdConfiguration,
  state: RouterStateSnapshot,
): boolean | UrlTree {
  const forbiddenUrl = oAuthService.getForbiddenUrlTree();
  if (!forbiddenUrl) {
    loggerService.logDebug(
      config,
      `There's no forbidden route configured, not redirecting to forbidden page.`,
    );
    return false;
  }
  if (state.url === forbiddenUrl.toString()) {
    loggerService.logDebug(
      config,
      `We're already on the forbidden page (${forbiddenUrl.toString()}) , not redirecting again.`,
    );

    return false;
  }

  loggerService.logDebug(
    config,
    `User is not allowed. Redirecting to forbidden page: ${forbiddenUrl.toString()}.`,
  );
  return forbiddenUrl;
}
